import { createContext, useContext, useState, useRef, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../hooks/hooks";
import { disconnectGoogle } from "../features/store/userSlice";
import { loginGoogle } from "../features/store/authSlice";
import { toast } from "react-toastify";
import { GoogleLogin } from "@react-oauth/google";

interface SocialLoginContextType {
  googleLoginEnabled: boolean;
  toggleGoogleLogin: () => Promise<void>;
  showGoogleConnectPrompt: boolean;
  setShowGoogleConnectPrompt: (show: boolean) => void;
  GoogleConnectButton: () => JSX.Element | null;
}

const SocialLoginContext = createContext<SocialLoginContextType | null>(null);

export const SocialLoginProvider = ({ children }: any) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const [showGoogleConnectPrompt, setShowGoogleConnectPrompt] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  // Google login is enabled if user has a google_id
  const googleLoginEnabled = Boolean(user?.google_id);

  const handleGoogleSuccess = async (credentialResponse: any) => {
    if (!credentialResponse?.credential) {
      toast.error("Google authentication failed.");
      return;
    }
    
    try {
      await dispatch(loginGoogle(credentialResponse.credential)).unwrap();
      toast.success("🎉 Google account linked successfully!");
      setShowGoogleConnectPrompt(false);
    } catch (error: any) {
      toast.error(error?.message || error || "Failed to link Google account.");
    }
  };

  const toggleGoogleLogin = async () => {
    try {
      if (googleLoginEnabled) {
        // Switch OFF: Disconnect/remove google_id
        await dispatch(disconnectGoogle()).unwrap();
        toast.success("Google account disconnected successfully");
      } else {
        // Switch ON: Programmatically click the hidden Google button
        if (googleButtonRef.current) {
          const button = googleButtonRef.current.querySelector('div[role="button"]');
          if (button) {
            (button as HTMLElement).click();
          } else {
            toast.error("Google authentication is not available. Please refresh the page.");
          }
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to update Google login settings");
    }
  };

  // Hidden Google Login button component
  const GoogleConnectButton = () => {
    if (googleLoginEnabled) return null;
    
    return (
      <div ref={googleButtonRef} style={{ position: 'absolute', left: '-9999px', opacity: 0 }}>
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={() => toast.error("Google authentication failed.")}
        />
      </div>
    );
  };

  return (
    <SocialLoginContext.Provider
      value={{
        googleLoginEnabled,
        toggleGoogleLogin,
        showGoogleConnectPrompt,
        setShowGoogleConnectPrompt,
        GoogleConnectButton,
      }}
    >
      {children}
      <GoogleConnectButton />
    </SocialLoginContext.Provider>
  );
};

export const useSocialLogin = () => {
  const context = useContext(SocialLoginContext);
  if (!context) {
    throw new Error("useSocialLogin must be used within SocialLoginProvider");
  }
  return context;
};